"use client";
import Image from "next/image";
import { Coffee, Heart, Minus, Reply, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PublicUserProfile } from "../types";
import Link from "next/link";
import { allowOnlyNumbers, cn, withInert } from "@/lib/utils";
import { phoneSchema, supportAmountSchema } from "@/lib/custom-zod-schema";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useEffect, useState } from "react";
import { handleSupport } from "./actions";
import { toast } from "sonner";
import { useParams } from "next/navigation";
import Script from "next/script";
import usePublicProfileStore from "../public-profile.store";
import dayjs from "dayjs";
import ShowMore from "@/components/common/show-more";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useReCaptcha } from "@/hooks/use-recaptcha";
import { Checkbox } from "@/components/ui/checkbox";

type CreatorProfilePropsType = {
  user: PublicUserProfile;
};

export const supportFormSchema = z.object({
  phone: phoneSchema,
  email: z.string().email("Email is invalid"),
  name: z.string().min(1, "Last name is required"),
  user_name: z.string().min(1, "user_name"),
  amount: supportAmountSchema,
  follower_message: z
    .string()
    .max(200, "only 200 characters allowed")
    .optional(),
  is_anonymous: z.boolean(),
});

export default function CreatorProfile({ user }: CreatorProfilePropsType) {
  const { userName } = useParams();
  const {
    generateReCaptchaToken,
    isLoading: isReCaptchaLoading,
    error: reCaptchaError,
  } = useReCaptcha();

  const { fetchSupporters, supporters } = usePublicProfileStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleIncrementByAmount = (amount: number) => {
    form.setValue("amount", form.getValues("amount") + amount);
  };

  const decrementAmount = () => {
    const amount = form.getValues("amount");
    if (amount > 1) {
      form.setValue("amount", amount - 1);
    }
  };

  const form = useForm<z.infer<typeof supportFormSchema>>({
    resolver: zodResolver(supportFormSchema),
    mode: "onChange",
    defaultValues: {
      phone: "",
      name: "",
      email: "",
      amount: +process.env.NEXT_PUBLIC_MINIMUM_SUPPORT_AMOUNT!,
      follower_message: "",
      user_name: decodeURIComponent(userName as string),
    },
  });

  useEffect(() => {
    if (userName) {
      form.setValue("user_name", decodeURIComponent(userName as string));
    }
  }, [userName, form]);

  async function onSubmit(values: z.infer<typeof supportFormSchema>) {
    try {
      setIsSubmitting(true);
      // Generate reCAPTCHA token with specific action
      const recaptchaToken = await generateReCaptchaToken();
      if (!recaptchaToken) {
        toast.error("Failed to verify you are human. Please try again.");
        setIsSubmitting(false);
        return;
      }
      await handleSupport(values, recaptchaToken);
      form.reset();
    } catch (error) {
      console.error("Error during form submission:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (user.id) fetchSupporters(user.id);
  }, [user.id, fetchSupporters]);

  return (
    <div className="container max-w-5xl bg-neutral-50 px-5 py-6 dark:bg-neutral-950/40">
      <Script
        id="razorpay-checkout-js"
        src="https://checkout.razorpay.com/v1/checkout.js"
      />
      {/* Banner and Profile */}
      <div className="relative mb-16">
        <div className="relative h-48 w-full overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-800 md:h-64">
          {user?.background_url && (
            <Image
              src={user?.background_url}
              alt="Profile banner"
              fill
              className="object-cover"
              key={user?.background_url} // Force re-render when image changes
              loading="eager"
              priority
              fetchPriority="high"
            />
          )}
        </div>
        <div className="absolute -bottom-12 left-8">
          <Avatar className="h-24 w-24 border-4 border-white">
            <AvatarImage src={user?.profile_url} alt="ProfileAvatar" />
            <AvatarFallback>
              {user?.full_name?.split(" ")?.map((w) => w[0]?.toUpperCase())}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>

      {/* Creator Info */}
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium">{user?.full_name}</h1>
          <div className="flex items-center gap-2">
            <p className="text-sm text-neutral-700 dark:text-neutral-200">
              {supporters?.total ?? "0"} Supporters
            </p>
            {/* <span className="text-neutral-700 dark:text-neutral-200">•</span>
            <p className="text-sm text-neutral-700 dark:text-neutral-200">
              Digital Artist
            </p> */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full md:grid md:grid-cols-[1fr_360px] md:gap-5">
        {/* Tabs */}
        <Tabs defaultValue="about" className="mb-8 w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 dark:border-neutral-700/50">
            <TabsTrigger
              value="about"
              className="rounded-none border-b-2 border-transparent px-4 py-2 shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              About
            </TabsTrigger>
            <TabsTrigger
              value="supporters"
              className="rounded-none border-b-2 border-transparent px-4 py-2 shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              Supporters
            </TabsTrigger>
          </TabsList>

          <TabsContent value="about" className="6">
            <div className="space-y-6">
              <div className="w-full">
                <h2 className="mb-3 text-lg font-medium">Bio</h2>
                <ShowMore
                  content={user?.bio}
                  allowedContentLength={300}
                  open={false}
                />
              </div>

              {user.instagram ||
              user.x ||
              user.youtube ||
              user.linkedin ||
              user.facebook ? (
                <div>
                  <h3 className="mb-2 text-sm font-medium">Links</h3>
                  <div className="flex flex-wrap gap-3">
                    {user.instagram && (
                      <Link
                        href={user.instagram}
                        target="_blank"
                        className="flex h-7 flex-row items-center rounded-full bg-pink-100 px-4 text-xs text-pink-500 dark:bg-pink-800 dark:text-white"
                      >
                        Instagram
                      </Link>
                    )}
                    {user.x && (
                      <Link
                        target="_blank"
                        href={user.x}
                        className="flex h-7 flex-row items-center rounded-full bg-black/50 px-4 text-xs text-white"
                      >
                        X
                      </Link>
                    )}
                    {user.youtube && (
                      <Link
                        target="_blank"
                        href={user.youtube}
                        className="flex h-7 flex-row items-center rounded-full bg-red-100 px-4 text-xs text-red-500 dark:bg-red-800 dark:text-white"
                      >
                        YouTube
                      </Link>
                    )}
                    {user.linkedin && (
                      <Link
                        target="_blank"
                        href={user.linkedin}
                        className="flex h-7 flex-row items-center rounded-full bg-sky-100 px-4 text-xs text-sky-500 dark:bg-sky-800 dark:text-white"
                      >
                        LinkedIn
                      </Link>
                    )}
                    {user.facebook && (
                      <Link
                        target="_blank"
                        href={user.facebook}
                        className="flex h-7 flex-row items-center rounded-full bg-blue-100 px-4 text-xs text-blue-500 dark:bg-blue-800 dark:text-white"
                      >
                        Facebook
                      </Link>
                    )}
                  </div>
                </div>
              ) : null}

              <div>
                <h3 className="mb-2 text-sm font-medium">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {user?.categories_name?.map((category: string) => (
                    <Badge
                      key={category}
                      variant="secondary"
                      className="min-h-8 rounded-full bg-white shadow dark:bg-neutral-800/80"
                    >
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="gallery" className="">
            <div className="space-y-6">
              <h2 className="text-lg font-medium">Gallery</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div
                    key={item}
                    className="group relative aspect-square overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-900"
                  >
                    <Image
                      src={`/placeholder.svg?height=300&width=300&text=Art ${item}`}
                      alt={`Gallery item ${item}`}
                      width={300}
                      height={300}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <p className="text-sm font-medium text-white">
                        Artwork Title {item}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" className="w-full">
                View All Works
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="posts" className="mt-6">
            <div className="space-y-6">
              <h2 className="text-lg font-medium">Latest Updates</h2>
              <div className="space-y-4">
                {[1, 2, 3].map((post) => (
                  <Card key={post} className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="p-4">
                        <div className="mb-2 flex items-center justify-between">
                          <h3 className="font-medium">
                            New Project Announcement
                          </h3>
                          <span className="text-xs text-neutral-700 dark:text-neutral-200">
                            {post} day{post > 1 ? "s" : ""} ago
                          </span>
                        </div>
                        <p className="text-sm text-neutral-700 dark:text-neutral-200">
                          I&quote;m excited to share that I&quote;m starting
                          work on a new series of illustrations inspired by
                          urban landscapes.
                        </p>
                      </div>
                      {post === 1 && (
                        <div className="aspect-video w-full bg-neutral-100 dark:bg-neutral-900">
                          <Image
                            src="/placeholder.svg?height=300&width=600&text=Post Image"
                            alt="Post image"
                            width={600}
                            height={300}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex border-t p-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs"
                        >
                          <Heart className="mr-1 h-3 w-3" /> 24 Likes
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-white dark:text-white"
                        >
                          <Coffee className="mr-1 h-3 w-3" /> Support
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="supporters" className="">
            <div className="space-y-6">
              <div className="no-scrollbar max-h-[300px] space-y-3 overflow-y-auto md:max-h-[500px]">
                {supporters?.orders?.length ? (
                  supporters?.orders?.map((supporter, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-lg border p-2 dark:border-neutral-700/50"
                    >
                      <div className="flex h-8 w-8 flex-row items-center justify-center overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-900">
                        {supporter.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="mb-1 flex items-center justify-between">
                          <p className="text-sm font-medium">
                            {supporter.name}
                          </p>
                          <Badge
                            variant="outline"
                            className="bg-neutral-50 text-neutral-700"
                          >
                            {supporter.symbol}
                            {supporter.amount}
                          </Badge>
                        </div>

                        {supporter?.message && (
                          <ShowMore
                            allowedContentLength={100}
                            content={supporter?.message ?? ""}
                            open={false}
                            contentClassName="font-semibold p-2"
                          />
                        )}
                        <div className="h-1" />
                        {supporter?.reply && (
                          <div className="flex flex-row items-start gap-2">
                            <Reply className="mt-2 rotate-180 text-neutral-500" />

                            <Avatar className="mt-2 h-6 w-6">
                              <AvatarImage src={user?.profile_url} />
                              <AvatarFallback>
                                {user.full_name.charAt(0)}
                              </AvatarFallback>
                            </Avatar>

                            <ShowMore
                              allowedContentLength={50}
                              content={supporter?.reply ?? ""}
                              open={false}
                              contentClassName="p-2"
                            />
                          </div>
                        )}
                        <p className="mt-1 text-end text-xs font-semibold text-neutral-900 dark:text-neutral-200">
                          {supporter.created_at
                            ? dayjs(supporter.created_at).format("DD MMM YYYY")
                            : ""}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500 dark:border-slate-700 dark:bg-neutral-800/50">
                    No supporters yet
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Donation Section */}
        <div className="space-y-4" id="support-btn">
          <Card
            inert={!user?.is_account_active}
            className={cn(
              "relative overflow-hidden border-none bg-neutral-100 shadow-sm dark:bg-neutral-900",
              !user?.is_account_active && "cursor-not-allowed",
            )}
            {...withInert(!user?.is_account_active)}
          >
            {!user?.is_account_active ? (
              <div className="absolute bottom-0 left-0 right-0 top-0 h-full w-full bg-slate-200/20 backdrop-blur-sm"></div>
            ) : null}
            <CardContent className="p-6">
              <h2 className="mb-2 text-base font-medium">
                Support {user?.first_name} {user?.last_name ?? ""}
              </h2>

              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="mb-2 space-y-2">
                  <div
                    className={`cursor-pointer rounded-lg border border-neutral-200 bg-white p-3 transition-colors dark:border-neutral-800 dark:bg-neutral-900/50`}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant={"outline"}
                        className="h-5 rounded-full border-0 bg-neutral-100 text-xs font-normal shadow-none hover:bg-neutral-700 hover:text-white dark:bg-neutral-800"
                        onClick={(e) => {
                          form.setValue("amount", 50);
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        type="button"
                        variant={"outline"}
                        className="h-5 rounded-full border-0 bg-neutral-100 text-xs font-normal shadow-none hover:bg-neutral-700 hover:text-white dark:bg-neutral-800"
                        onClick={(e) => {
                          handleIncrementByAmount(10);
                        }}
                      >
                        +10
                      </Button>
                      <Button
                        type="button"
                        variant={"outline"}
                        className="h-5 rounded-full border-0 bg-neutral-100 text-xs font-normal shadow-none hover:bg-neutral-700 hover:text-white dark:bg-neutral-800"
                        onClick={(e) => {
                          handleIncrementByAmount(50);
                        }}
                      >
                        +50
                      </Button>
                      <Button
                        type="button"
                        variant={"outline"}
                        className="h-5 rounded-full border-0 bg-neutral-100 text-xs font-normal shadow-none hover:bg-neutral-700 hover:text-white dark:bg-neutral-800"
                        onClick={(e) => {
                          handleIncrementByAmount(100);
                        }}
                      >
                        +100
                      </Button>
                    </div>
                    <div className="mt-2 flex items-center gap-1">
                      <Button
                        aria-label="Minus"
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-10 rounded-r-none border-neutral-200 bg-neutral-200 dark:border-neutral-800 dark:bg-neutral-800"
                        onClick={(e) => {
                          decrementAmount();
                        }}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <Input
                        type="text"
                        value={form.watch("amount")}
                        className="h-8 flex-1 rounded-none border-neutral-200 text-center shadow-none dark:border-neutral-800"
                        autoComplete="false"
                        onChange={({ target: { value } }) => {
                          if (Number.isNaN(+value)) form.setValue("amount", 0);
                          else form.setValue("amount", +value);
                        }}
                        placeholder="Show your support"
                        inputMode="numeric" // Guide the virtual keyboard to numbers
                        pattern="\d*" // Suggest numeric pattern, though not enforced on type
                        onKeyDown={allowOnlyNumbers}
                      />

                      <Button
                        aria-label="Plus"
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-10 rounded-l-none border-neutral-200 bg-neutral-200 dark:border-neutral-800 dark:bg-neutral-800"
                        onClick={(e) => {
                          handleIncrementByAmount(1);
                        }}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    {form.formState.errors?.amount && (
                      <p className="ml-1 text-[10px] text-red-500">
                        {form.formState.errors?.amount?.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <Input
                    {...form.register("name")}
                    placeholder="Your name or nickname"
                    className="h-9 bg-white text-xs shadow-none placeholder:text-xs dark:border-neutral-700/50 dark:bg-neutral-800"
                  />
                  {form.formState.errors?.name && (
                    <p className="ml-1 text-[10px] text-red-500">
                      {form.formState.errors?.name?.message}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <Input
                    {...form.register("email")}
                    placeholder="Your email"
                    className="h-9 bg-white text-xs shadow-none placeholder:text-xs dark:border-neutral-700/50 dark:bg-neutral-800"
                  />
                  {form.formState.errors?.email && (
                    <p className="ml-1 text-[10px] text-red-500">
                      {form.formState.errors?.email?.message}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <Input
                    {...form.register("phone")}
                    placeholder="Your phone number"
                    className="h-9 bg-white text-xs shadow-none placeholder:text-xs dark:border-neutral-700/50 dark:bg-neutral-800"
                  />
                  {form.formState.errors?.phone && (
                    <p className="ml-1 text-[10px] text-red-500">
                      {form.formState.errors?.phone?.message}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <Textarea
                    {...form.register("follower_message")}
                    placeholder="Leave a message (optional)"
                    className="resize-none bg-white text-xs shadow-none placeholder:text-xs dark:border-neutral-700/50 dark:bg-neutral-800"
                  />
                  {form.formState.errors?.follower_message && (
                    <p className="ml-1 text-[10px] text-red-500">
                      {form.formState.errors?.follower_message?.message}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="is_anonymous"
                    className="flex flex-row items-center gap-1"
                  >
                    <input
                      id="is_anonymous"
                      type="checkbox"
                      className="h-3.5 w-3.5 cursor-pointer rounded-lg"
                      {...form.register("is_anonymous")}
                    />

                    <span className="cursor-pointer select-none text-xs">
                      Donate as an anonymous?
                    </span>
                  </label>

                  {form.formState.errors?.is_anonymous && (
                    <p className="ml-1 text-[10px] text-red-500">
                      {form.formState.errors?.is_anonymous as ReactNode}
                    </p>
                  )}
                </div>
                {/* bg-neutral-800 dark:bg-white */}
                <Button
                  type="submit"
                  disabled={isSubmitting || isReCaptchaLoading}
                  className="w-full bg-gradient-to-tr from-blue-500 via-cyan-500 to-teal-500 text-white hover:bg-gradient-to-tr hover:from-blue-500 hover:via-cyan-600 hover:to-teal-500"
                >
                  {isSubmitting || isReCaptchaLoading
                    ? "Processing..."
                    : `Support ₹${form.watch("amount")}`}
                </Button>
                {reCaptchaError && (
                  <p className="mt-2 text-center text-xs text-red-500">
                    {reCaptchaError}
                  </p>
                )}
              </form>

              <p className="mt-3 text-center text-[9px] text-black dark:text-white">
                By supporting, you agree to our <br />
                <Link href={"/privacy-policy"} className="text-blue-500">
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link href={"/terms-and-condition"} className="text-blue-500">
                  Terms of Service
                </Link>
                .
              </p>

              <div className="mt-4 text-center text-xs text-neutral-700 dark:text-neutral-200">
                Payments are secure and encrypted
              </div>
              <div className="mt-4 text-center text-[9px] text-neutral-700 dark:text-neutral-200">
                This site is protected by reCAPTCHA and the Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  className="text-blue-500"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://policies.google.com/terms"
                  className="text-blue-500"
                >
                  Terms of Service
                </a>{" "}
                apply.
              </div>

              <div className="mt-6">
                <div className="flex justify-center gap-4">
                  <div className="h-8 w-10">
                    <Image
                      src={`https://${process.env.NEXT_PUBLIC_S3_BUCKET_NAME}.s3.ap-south-1.amazonaws.com/miscellaneous/upi.png`}
                      alt="UPI"
                      width={80}
                      height={30}
                      className="h-auto w-auto rounded bg-white object-contain"
                    />
                  </div>
                  <div className="h-8 w-10">
                    <Image
                      src={`https://${process.env.NEXT_PUBLIC_S3_BUCKET_NAME}.s3.ap-south-1.amazonaws.com/miscellaneous/visa.png`}
                      alt="Visa"
                      width={40}
                      height={24}
                      className="h-auto w-auto rounded bg-white object-contain"
                    />
                  </div>
                  <div className="h-8 w-10">
                    <Image
                      src={`https://${process.env.NEXT_PUBLIC_S3_BUCKET_NAME}.s3.ap-south-1.amazonaws.com/miscellaneous/mastercard.png`}
                      alt="Mastercard"
                      width={40}
                      height={24}
                      className="h-auto w-auto rounded bg-white object-contain"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Membership Benefits */}
          {/* <Card className="overflow-hidden border-none shadow-sm">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <Gift className="h-5 w-5 text-neutral-800" />
                <h3 className="font-medium">Membership Benefits</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <div className="mt-1 h-4 w-4 rounded-full bg-neutral-100 text-center text-[10px] font-bold text-neutral-600">
                    ✓
                  </div>
                  <span className="text-sm">
                    Exclusive supporter-only posts and updates
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="mt-1 h-4 w-4 rounded-full bg-neutral-100 text-center text-[10px] font-bold text-neutral-600">
                    ✓
                  </div>
                  <span className="text-sm">
                    Early access to new content and releases
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="mt-1 h-4 w-4 rounded-full bg-neutral-100 text-center text-[10px] font-bold text-neutral-600">
                    ✓
                  </div>
                  <span className="text-sm">
                    Monthly digital downloads and resources
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="mt-1 h-4 w-4 rounded-full bg-neutral-100 text-center text-[10px] font-bold text-neutral-600">
                    ✓
                  </div>
                  <span className="text-sm">
                    Name in credits and special thanks
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="mt-1 h-4 w-4 rounded-full bg-neutral-100 text-center text-[10px] font-bold text-neutral-600">
                    ✓
                  </div>
                  <span className="text-sm">
                    Access to supporter-only live streams
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card> */}

          {/* Recent Milestones */}
          {/* <Card className="overflow-hidden border-none shadow-sm">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <Award className="h-5 w-5 text-amber-500" />
                <h3 className="font-medium">Recent Milestones</h3>
              </div>
              <div className="relative border-l-2 border-dashed border-muted pl-4 before:absolute before:-left-[5px] before:top-0 before:h-3 before:w-3 before:rounded-full before:bg-neutral-800 after:absolute after:-left-[5px] after:bottom-0 after:h-3 after:w-3 after:rounded-full after:bg-neutral-800">
                <div className="space-y-4 py-2">
                  <div className="relative before:absolute before:-left-[21px] before:top-1 before:h-3 before:w-3 before:rounded-full before:bg-neutral-800 before:ring-4 before:ring-neutral-100">
                    <p className="text-sm font-medium">
                      Reached 1,000 followers
                    </p>
                    <p className="text-xs text-neutral-700 dark:text-neutral-200">
                      February 2025
                    </p>
                  </div>
                  <div className="relative before:absolute before:-left-[21px] before:top-1 before:h-3 before:w-3 before:rounded-full before:bg-neutral-800 before:ring-4 before:ring-neutral-100">
                    <p className="text-sm font-medium">
                      Launched new tutorial series
                    </p>
                    <p className="text-xs text-neutral-700 dark:text-neutral-200">
                      January 2025
                    </p>
                  </div>
                  <div className="relative before:absolute before:-left-[21px] before:top-1 before:h-3 before:w-3 before:rounded-full before:bg-neutral-800 before:ring-4 before:ring-neutral-100">
                    <p className="text-sm font-medium">First 100 supporters</p>
                    <p className="text-xs text-neutral-700 dark:text-neutral-200">
                      December 2024
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>
    </div>
  );
}

{
  /* <div className="mb-4 flex rounded-md">
                <Button
                  variant={paymentType === "onetime" ? "default" : "outline"}
                  className={`flex-1 rounded-r-none  ${paymentType === "onetime" ? "bg-neutral-900" : "bg-neutral-100"} `}
                  onClick={() => setPaymentType("onetime")}
                >
                  One time
                </Button>
                <Button
                  variant={paymentType === "monthly" ? "default" : "outline"}
                  className={`flex-1 rounded-l-none  ${paymentType === "monthly" ? "bg-neutral-900" : "bg-neutral-100"} `}
                  onClick={() => setPaymentType("monthly")}
                >
                  Monthly
                </Button>
              </div> */
}
